/**
 * GoalBullet — bullet-chart visualization of a single goal, showing the
 * latest value vs the target with qualitative bands.
 *
 * Replaces the line-trend default on the Goals page (Phase D1). The line
 * trend is still available behind an "Expand history" toggle via
 * `GoalTrackingChart`.
 *
 * Pre-fetches the latest FY's value for the goal's metric (using the same
 * endpoint resolution as GoalTrackingChart so speed/duration goals hit the
 * dedicated marts instead of falling back to award_count).
 */
import { useEffect, useMemo, useState } from 'react'
import { api } from '../../services/api'
import type { Goal, MeasureDefinition } from '../../types/exec'
import BulletRenderer from './generic/BulletRenderer'
import type { GroupByDimension, MetricKey } from './generic/types'

interface PerfRow { [key: string]: any }

interface Props {
  goal: Goal
  measureDef?: MeasureDefinition
  /** Optional callback receiving the resolved (latest, prior) values for
   *  external use (e.g., status chip). */
  onResolved?: (latest: number | null, prior: number | null) => void
}

/** Endpoint resolver shared with GoalTrackingChart. Kept inline to avoid
 *  introducing a circular dep — refactor into a shared helper later if more
 *  goal surfaces grow. */
const SPEED_KPI_FIELDS = new Set([
  'p25_solicitation_to_award', 'p50_solicitation_to_award',
  'p75_solicitation_to_award', 'p90_solicitation_to_award',
])
const DURATION_KPI_FIELDS = new Set([
  'p25_duration_days', 'p50_duration_days', 'p75_duration_days', 'p90_duration_days',
  'avg_duration_days', 'min_duration_days', 'max_duration_days',
])
const PERFORMANCE_FALLBACK_MAP: Record<string, MetricKey> = {
  full_open_pct: 'full_open_pct',
  sb_dollar_pct: 'sb_dollar_pct',
  set_aside_pct: 'set_aside_pct',
  sb_award_pct: 'sb_award_pct',
  q4_share_of_fy: 'award_count',
  total_awards: 'award_count',
  award_count: 'award_count',
  // Phase M.M1 added ot_award_pct as a real rate column on /performance/.
  // Was previously falling back to award_count, which made bullets render
  // raw counts (≈150,000) instead of the % the goal target is in.
  ot_award_pct: 'ot_award_pct',
}

function resolveEndpoint(kpiField: string | null | undefined): { url: string; field: string; isDays: boolean } {
  if (!kpiField) return { url: '/api/govdata/performance/', field: 'award_count', isDays: false }
  if (SPEED_KPI_FIELDS.has(kpiField)) return { url: '/api/govdata/speed-to-contract/', field: kpiField, isDays: true }
  if (DURATION_KPI_FIELDS.has(kpiField)) return { url: '/api/govdata/contract-duration/', field: kpiField, isDays: true }
  return { url: '/api/govdata/performance/', field: PERFORMANCE_FALLBACK_MAP[kpiField] || 'award_count', isDays: false }
}

export default function GoalBullet({ goal, measureDef, onResolved }: Props) {
  const endpoint = useMemo(() => resolveEndpoint(measureDef?.kpi_field), [measureDef?.kpi_field])

  const [data, setData] = useState<PerfRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const params: Record<string, string> = {}
    if (endpoint.url === '/api/govdata/performance/') params.group_by = 'fiscal_year'
    if (goal.portfolio_group) params.portfolio_group = goal.portfolio_group

    api
      .getRaw<{ results: PerfRow[] }>(endpoint.url, params)
      .then((resp) => setData(resp.results))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [goal.portfolio_group, endpoint.url])

  // Aggregate per-FY (count-weighted average for percentile endpoints).
  const byFy = useMemo(() => {
    const buckets = new Map<number, number>()
    if (endpoint.url === '/api/govdata/performance/') {
      for (const r of data) {
        const fy = Number(r.fiscal_year)
        if (Number.isNaN(fy) || fy >= 2026) continue
        const v = Number(r[endpoint.field])
        if (!Number.isNaN(v)) buckets.set(fy, v)
      }
    } else {
      const accum = new Map<number, { sum: number; weight: number }>()
      for (const r of data) {
        const fy = Number(r.fiscal_year)
        if (Number.isNaN(fy) || fy >= 2026) continue
        const v = Number(r[endpoint.field])
        if (Number.isNaN(v)) continue
        const w = Number(r.award_count ?? 1) || 1
        if (!accum.has(fy)) accum.set(fy, { sum: 0, weight: 0 })
        const a = accum.get(fy)!
        a.sum += v * w
        a.weight += w
      }
      for (const [fy, a] of accum) buckets.set(fy, a.weight > 0 ? a.sum / a.weight : 0)
    }
    return buckets
  }, [data, endpoint.url, endpoint.field])

  const latestFy = useMemo(() => {
    if (byFy.size === 0) return null
    return Math.max(...byFy.keys())
  }, [byFy])

  // Notify parent with (latest, prior) for chip rendering.
  useEffect(() => {
    if (latestFy === null) {
      onResolved?.(null, null)
      return
    }
    const latest = byFy.get(latestFy) ?? null
    const prior = byFy.get(latestFy - 1) ?? null
    onResolved?.(latest, prior)
  }, [latestFy, byFy, onResolved])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
      </div>
    )
  }

  if (latestFy === null) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-gray-400">
        No data for this portfolio
      </div>
    )
  }

  // Synthetic single-bar dataset for BulletRenderer (one row = "Current FY").
  const latestValue = byFy.get(latestFy) ?? 0
  const syntheticData: PerfRow[] = [
    { fiscal_year: latestFy, [endpoint.field]: latestValue },
  ]

  // Goal target is stored in the unit the leader thinks in ("15%", "90 days")
  // but the API returns rate columns as fractions (0–1). The bullet's bar and
  // the target markLine have to share units, so convert the percent-unit
  // target down to a fraction when the underlying metric is a *_pct column.
  const targetInDataUnits =
    goal.target_value.unit === 'percent' ? goal.target_value.value / 100 : goal.target_value.value

  const unitSuffix =
    goal.target_value.unit === 'percent' ? '%' : goal.target_value.unit === 'days' ? 'd' : ''

  return (
    <BulletRenderer
      data={syntheticData}
      groupBy={'fiscal_year' as GroupByDimension}
      metrics={[endpoint.field as MetricKey]}
      target={targetInDataUnits}
      bands={[0.7, 0.95]}
      direction={goal.target_value.direction}
      targetLabel={`Target: ${goal.target_value.value}${unitSuffix}`}
    />
  )
}
